// Renders one animated "heat-score" bar-graph panel per locked-on ANIMAL and
// keeps the set in sync with which animals are currently locked. Panels for
// animals that are no longer present are removed.
//
// IMPORTANT: this whole overlay is an EXPLICIT NOVELTY. The "estrus / on-heat
// likelihood" percentage is a playful fringe-science gimmick, NOT a real
// biological measurement. It applies to animals ONLY (never humans), and every
// panel carries a persistent "NOVELTY - not real science" caption. The score's
// own disclaimer text is surfaced as a hover tooltip (title attribute).

/**
 * A single novelty heat score for one animal.
 *
 * NON-SCIENTIFIC: `percent` is a made-up "on-heat likelihood" value; it must
 * never be presented as a real measurement. `disclaimer` always carries the
 * explanatory not-real-science text.
 */
export interface HeatScore {
  /** 0..100 novelty "on-heat likelihood" percentage. */
  percent: number;
  /** Short human label for the score (for example "estrus likelihood"). */
  label: string;
  /** Explicit not-real-science disclaimer, surfaced as a tooltip. */
  disclaimer: string;
  /** Top contributing fringe-science factors (name + its contribution). */
  factors: { name: string; contribution: number }[];
}

/** One locked animal paired with its novelty heat score. */
export interface HeatItem {
  /** Stable track id for the animal. */
  id: number;
  /** Species label (for example "cat", "dog", "cow"). */
  species: string;
  /** The novelty heat score to visualise. */
  score: HeatScore;
}

/**
 * Maintains a vertical stack of heat-score panels, one per locked animal.
 * Mirrors the add / update / remove keyed-by-id pattern used by `Cards`.
 */
export class HeatBar {
  /** Live panels keyed by animal id, so we can update in place and prune. */
  private els = new Map<number, HTMLElement>();

  /**
   * @param container Host element the panels are appended into. The integrator
   *   is responsible for positioning this container in the HUD.
   */
  constructor(private container: HTMLElement) {}

  /**
   * Sync the set of bars to the currently locked animals: add panels for new
   * ids, update existing ones in place (so CSS width transitions animate
   * smoothly), and remove panels for ids that are no longer present.
   *
   * @param items The currently locked animals with their novelty scores.
   */
  sync(items: HeatItem[]): void {
    const live = new Set(items.map((it) => it.id));

    // Remove panels for animals no longer locked.
    for (const [id, el] of this.els) {
      if (!live.has(id)) {
        el.remove();
        this.els.delete(id);
      }
    }

    for (const it of items) {
      let el = this.els.get(it.id);
      if (!el) {
        // First time we see this id: create the shell once. Rebuilding the
        // whole panel every frame would restart the width animation, so we
        // keep the shell stable and only patch dynamic parts in update().
        el = this.build(it);
        this.container.appendChild(el);
        this.els.set(it.id, el);
      }
      this.update(el, it);
    }
  }

  /**
   * Create the static shell for one panel. Dynamic values (percent, bar width,
   * color, factors) are filled in by `update` so that re-syncs animate rather
   * than snapping.
   */
  private build(it: HeatItem): HTMLElement {
    const panel = document.createElement("article");
    panel.className = "heat-panel";

    panel.innerHTML = `
      <div class="heat-head">
        <span class="heat-id">🐾 #${it.id}</span>
        <span class="heat-species"></span>
      </div>
      <div class="heat-scoreline">
        <span class="heat-percent">0%</span>
        <span class="heat-scorelabel"></span>
      </div>
      <div class="heat-track">
        <div class="heat-fill" style="width:0%"></div>
      </div>
      <div class="heat-factors"></div>
      <div class="heat-caption">NOVELTY - not real science</div>`;

    return panel;
  }

  /**
   * Patch the dynamic parts of an existing panel in place. Setting the fill
   * element's inline width (rather than replacing the node) lets the CSS
   * `transition` animate the bar between values.
   */
  private update(panel: HTMLElement, it: HeatItem): void {
    const s = it.score;
    // Clamp to a sane 0..100 range so a bad scorer value cannot break layout.
    const pct = clamp(s.percent, 0, 100);

    // The score disclaimer is surfaced as a tooltip on the whole panel.
    panel.title = s.disclaimer;

    const species = panel.querySelector<HTMLElement>(".heat-species");
    if (species) species.textContent = it.species;

    const percentEl = panel.querySelector<HTMLElement>(".heat-percent");
    if (percentEl) {
      percentEl.textContent = `${Math.round(pct)}%`;
      percentEl.style.color = rampColor(pct);
    }

    const scoreLabel = panel.querySelector<HTMLElement>(".heat-scorelabel");
    if (scoreLabel) scoreLabel.textContent = s.label;

    const fill = panel.querySelector<HTMLElement>(".heat-fill");
    if (fill) {
      // Width drives the animated bar; color ramps green -> amber -> red.
      fill.style.width = `${pct}%`;
      fill.style.background = rampColor(pct);
      fill.style.boxShadow = `0 0 10px ${rampColor(pct)}`;
    }

    const factorsEl = panel.querySelector<HTMLElement>(".heat-factors");
    if (factorsEl) factorsEl.innerHTML = this.factorChips(s.factors);
  }

  /**
   * Render the top 3 to 5 contributing factors as small chips, sorted by
   * contribution (largest first). Contribution is shown as a rounded percent.
   */
  private factorChips(factors: { name: string; contribution: number }[]): string {
    const top = [...factors]
      .sort((a, b) => b.contribution - a.contribution)
      .slice(0, 5);

    return top
      .map((f) => {
        const value = `${Math.round(clamp(f.contribution, 0, 100))}%`;
        return `<span class="heat-chip">${escapeHtml(f.name)}<b>${value}</b></span>`;
      })
      .join("");
  }
}

/** Clamp a number into the inclusive [lo, hi] range. */
function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/**
 * Map a 0..100 percentage to a color on a green (low) to amber (mid) to red
 * (high) ramp. Uses HSL hue interpolation from 140deg (green) down to 0deg
 * (red), which passes through amber around the midpoint.
 */
function rampColor(pct: number): string {
  const t = clamp(pct, 0, 100) / 100;
  const hue = 140 - 140 * t;
  return `hsl(${hue.toFixed(0)}, 90%, 55%)`;
}

/** Escape text destined for innerHTML (matches the helper in cards.ts). */
function escapeHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] ?? c);
}
